import React, { useContext } from 'react';
import withAbortRequest, { AbortRequestPropsType } from '../../../core/hoc/withAbortRequest';
import { InfoMessageService, TranslationService } from '../../../core/services';
import { Alert, Icon, Loader, ModuleContainer } from '../../../core/components';
import ApiProvider from '../../../core/api/ApiProvider';
import Status from '../../../core/types/Status';
import styles from './UpcommingPaymentsTile.module.scss';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../../AppContext';
import { useQuery } from 'react-query';

type UpcomingPaymentTileVM = {
  paymentDueDateFormatted: string;
  paymentDueDate: string;
  paymentAmount: string;
  paymentCurrency: string;
  caseNo: string;
};

type UpcomingPaymentsTileVM = {
  isEnabled: boolean;
  upcomingPaymentsList: UpcomingPaymentTileVM[];
  currency: string;
};

const UpcomingPaymentsTile = (props: AbortRequestPropsType) => {
  const context = useContext(AppContext);
  const history = useHistory();

  const availablePaymentProviders = useQuery<Status<UpcomingPaymentsTileVM>>(
    'availablePaymentProviders',
    () =>
      ApiProvider.get<Status<UpcomingPaymentsTileVM>>(
        'UpcomingPaymentsTileModule/GetUpcomingPaymentsTile',
        props.cancelTokenSource.token
      ),
    {
      enabled: !context.isHideMypageCases,
      refetchOnWindowFocus: false,
      onError: () => {
        InfoMessageService.error(TranslationService.translate('ErrorSthWentWrong'));
      },
    }
  );

  const isEnabled = availablePaymentProviders.data?.data.isEnabled;

  const onClick = () => {
    history.push('/UpcomingPayments/Index');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter') {
      onClick();
    }
  };

  return (
    <>
      {isEnabled && (
        <ModuleContainer classNames={styles.wrapper}>
          <ModuleContainer.Header
            id="UpcomingPaymentsLabel"
            classNames={styles.header}
            title={TranslationService.translate('UpcomingPayments')}
            headingLevel="h2"
          >
            <span tabIndex={0} onKeyDown={handleKeyDown}>
              <Icon
                iconVariant={Icon.Variants.FontAwesome}
                className={styles.headerIcon}
                isPointer={true}
                icon="fas fa-arrow-right"
                onClick={onClick}
              />
            </span>
          </ModuleContainer.Header>

          <ModuleContainer.Body
            variant={
              availablePaymentProviders.isFetching ||
              (availablePaymentProviders.data?.data &&
                availablePaymentProviders.data?.data.upcomingPaymentsList &&
                availablePaymentProviders.data?.data.upcomingPaymentsList.length === 0)
                ? ModuleContainer.Body.BodyVariant.Primary
                : ModuleContainer.Body.BodyVariant.Secondary
            }
          >
            {availablePaymentProviders.isFetching && <Loader opacity={0.4} />}
            {!availablePaymentProviders.isFetching &&
              availablePaymentProviders.data?.data &&
              availablePaymentProviders.data?.data.upcomingPaymentsList &&
              availablePaymentProviders.data?.data.upcomingPaymentsList.length === 0 && (
                <Alert
                  className="mb-0"
                  variant={Alert.Variants.Info}
                  text={TranslationService.translate('NoUpcomingPayments')}
                />
              )}
            <ul className={styles.list}>
              {availablePaymentProviders.data?.data.upcomingPaymentsList &&
                availablePaymentProviders.data?.data.upcomingPaymentsList.map((payment, index) => (
                  <li className={styles.listItem} key={index}>
                    <div>
                      <div className={styles.paymentDate}>{payment.paymentDueDateFormatted}</div>

                      <div className={styles.paymentCase}>
                        {TranslationService.translate('CaseNr')}: {payment.caseNo}
                      </div>
                    </div>

                    <div className={styles.paymentAmount}>
                      {payment.paymentAmount} {availablePaymentProviders.data?.data.currency}
                    </div>
                  </li>
                ))}
            </ul>
          </ModuleContainer.Body>
        </ModuleContainer>
      )}
    </>
  );
};

export default withAbortRequest(UpcomingPaymentsTile);
